'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import Image from 'next/image';
import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import Pin, { setPinsHidden,setPinsVisible, animatePinsDrop } from "@/components/icon/pin";
import { ASSET_PREFIX } from '@/constants/routes';
import { stateLocations } from "@/data/static-data";
import useImageLoad from '@/hooks/useImageLoad';


const PAN_STEP = 50;

const IMAGE_WIDTH = 5820;
const IMAGE_HEIGHT = 4090;
const IMAGE_ASPECT = IMAGE_WIDTH / IMAGE_HEIGHT;

const POPOVER_ID = 'map-card-popover';

/**
 * Interactive map component with zoom and pan functionality.
 * The map image always covers its container while maintaining aspect ratio.
 * Supports mouse/touch dragging and scroll wheel panning.
 * @param {Object} props
 * @param {number} [props.zoomLevel=1] - Current zoom level (1 = no zoom)
 * @param {Function} [props.onPinClick] - Called with (index, pin) when a pin is clicked
 * @param {number|null} [props.selectedPinIndex] - Index of the currently selected pin
 * @param {Function} [props.onInteractionStart] - Called when user starts panning/zooming/keyboard nav
 */
const Map = forwardRef(function Map({ isDummy = false, zoomLevel = 1, zoomScale = 1, stories, onPinClick, onPinFocus, onPinMouseEnter, onPinMouseLeave, selectedPinIndex, onInteractionStart, onDoubleClickZoom }, ref) {
  const containerRef = useRef(null);
  const pinsRef = useRef([]);
  const lastTapRef = useRef(0);

  const pinLocations = stories.map(story => story.mapLocation)

  useImperativeHandle(ref, () => ({
    focus: () => containerRef.current?.focus(),
    getPinRect: (index) => pinsRef.current[index]?.getBoundingClientRect() ?? null,
    focusPin: (index) => pinsRef.current[index]?.focus(),
    pinCount: pinLocations.length,
  }));
  const prevZoomRef = useRef(zoomScale);

  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [maxPan, setMaxPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [containedSize, setContainedSize] = useState({ width: 0, height: 0 });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [allowMotion, setAllowMotion] = useState(false);
  const [transitionComplete, setTransitionComplete] = useState(false);

  /**
   * Calculates dimensions needed to cover the container while maintaining aspect ratio.
   * @param {number} [zoom=1] - Zoom level to factor into calculations
   * @returns {{ scale: number, containedSize: { width: number, height: number }, maxPan: { x: number, y: number } }}
   */
  const calculateDimensions = useCallback((zoom = 1) => {
    const container = containerRef.current;
    if (!container) return { scale: 1, maxPan: { x: 0, y: 0 }, containedSize: { width: 0, height: 0 } };

    const containerRect = container.getBoundingClientRect();
    const containerAspect = containerRect.width / containerRect.height;

    const isWidthConstrained = IMAGE_ASPECT > containerAspect;
    const containedWidth = isWidthConstrained ? containerRect.width : containerRect.height * IMAGE_ASPECT;
    const containedHeight = isWidthConstrained ? containerRect.width / IMAGE_ASPECT : containerRect.height;

    const scaleX = containerRect.width / containedWidth;
    const scaleY = containerRect.height / containedHeight;
    const coverScale = Math.max(scaleX, scaleY);

    const totalScale = coverScale * zoom;
    const displayedWidth = containedWidth * totalScale;
    const displayedHeight = containedHeight * totalScale;

    return {
      scale: coverScale,
      containedSize: { width: containedWidth, height: containedHeight },
      maxPan: {
        x: Math.max(0, (displayedWidth - containerRect.width) / 2),
        y: Math.max(0, (displayedHeight - containerRect.height) / 2),
      }
    };
  }, []);

  /**
   * Constrains pan position within allowed bounds.
   * @param {{ x: number, y: number }} pan - Pan position to constrain
   * @param {{ x: number, y: number }} bounds - Maximum pan bounds
   * @returns {{ x: number, y: number }} Constrained pan position
   */
  const constrainPan = useCallback((pan, bounds) => ({
    x: Math.max(-bounds.x, Math.min(bounds.x, pan.x)),
    y: Math.max(-bounds.y, Math.min(bounds.y, pan.y)),
  }), []);

  const hasSetInitialPanRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setTransitionComplete(true), 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const updateDimensions = (isResize = false) => {
      const { scale: newScale, maxPan: newMaxPan, containedSize: newContainedSize } = calculateDimensions(zoomScale);
      setScale(newScale);
      setMaxPan(newMaxPan);
      setContainedSize(newContainedSize);
      if (!hasSetInitialPanRef.current) {
        hasSetInitialPanRef.current = true;
        setPanPosition({ x: 0, y: newMaxPan.y });
      } else if (isResize) {
        setPanPosition(prev => constrainPan(prev, newMaxPan));
      }
    };

    updateDimensions();

    const handleResize = () => updateDimensions(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDimensions, constrainPan, zoomScale]);

  useEffect(() => {
    requestAnimationFrame(() => {
      setHasInitialized(true);
    });
  }, []);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      setAllowMotion(true);

      const pins = pinsRef.current.filter(Boolean);
      setPinsHidden(pins);

      // Animate pins dropping from center
      animatePinsDrop(pins, {
        staggerFrom: 'center',
        positions: pinLocations,
        baseDelay: 0.3,
        staggerDelay: 0.015,
      });

      return () => setAllowMotion(false);
    });

    mm.add('(prefers-reduced-motion: reduce)', () => {
      setAllowMotion(false);

      const pins = pinsRef.current.filter(Boolean);
      setPinsVisible(pins)


      return () => setAllowMotion(false);
    });
  }, []);

  useEffect(() => {
    if (prevZoomRef.current !== zoomScale) {
      const zoomRatio = zoomScale / prevZoomRef.current;
      prevZoomRef.current = zoomScale;

      const { maxPan: newMaxPan } = calculateDimensions(zoomScale);

      setPanPosition(prev => constrainPan({
        x: prev.x * zoomRatio,
        y: prev.y * zoomRatio,
      }, newMaxPan));
    }
  }, [zoomScale, constrainPan, calculateDimensions]);

  /**
   * Extracts client coordinates from mouse or touch event.
   * @param {MouseEvent|TouchEvent} e
   * @returns {{ x: number, y: number }}
   */
  const getClientCoords = (e) => ({
    x: e.touches ? e.touches[0].clientX : e.clientX,
    y: e.touches ? e.touches[0].clientY : e.clientY,
  });

  const canDrag = maxPan.x > 0 || maxPan.y > 0 || zoomScale > 1;

  const handleDragStart = (e) => {
    if (!canDrag) return;
    if (e.target.closest('button')) return;
    onInteractionStart?.();
    setIsDragging(true);
    const { x, y } = getClientCoords(e);
    setDragStart({ x: x - panPosition.x, y: y - panPosition.y });
  };

  const handleDragMove = (e) => {
    if (!isDragging || !canDrag) return;
    const { x, y } = getClientCoords(e);
    setPanPosition(constrainPan({ x: x - dragStart.x, y: y - dragStart.y }, maxPan));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const zoomTowardPoint = (clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;

    const newZoomScale = onDoubleClickZoom?.();
    if (newZoomScale == null) return;

    const rect = container.getBoundingClientRect();
    const offset = {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };

    const zoomRatio = newZoomScale / zoomScale;
    const { maxPan: newMaxPan } = calculateDimensions(newZoomScale);
    prevZoomRef.current = newZoomScale;

    setPanPosition(prev => constrainPan({
      x: offset.x * (1 - zoomRatio) + prev.x * zoomRatio,
      y: offset.y * (1 - zoomRatio) + prev.y * zoomRatio,
    }, newMaxPan));
  };

  const handleDoubleClick = (e) => {
    if (e.target.closest('button')) return;
    zoomTowardPoint(e.clientX, e.clientY);
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      if (!e.target.closest('button')) {
        const touch = e.changedTouches[0];
        if (touch) zoomTowardPoint(touch.clientX, touch.clientY);
      }
    } else {
      lastTapRef.current = now;
    }
  };

  const handleWheel = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    onInteractionStart?.();
    setPanPosition(prev => constrainPan({ x: prev.x - e.deltaX, y: prev.y - e.deltaY }, maxPan));
  };

  /**
   * Handles keyboard navigation for panning.
   * @param {KeyboardEvent} e
   */
  const handleKeyDown = (e) => {
    if (!canDrag) return;

    const panAmount = e.shiftKey ? PAN_STEP * 2 : PAN_STEP;
    let newPan = null;

    switch (e.key) {
    case 'ArrowUp':
      newPan = { x: panPosition.x, y: panPosition.y + panAmount };
      break;
    case 'ArrowDown':
      newPan = { x: panPosition.x, y: panPosition.y - panAmount };
      break;
    case 'ArrowLeft':
      newPan = { x: panPosition.x + panAmount, y: panPosition.y };
      break;
    case 'ArrowRight':
      newPan = { x: panPosition.x - panAmount, y: panPosition.y };
      break;
    default:
      return;
    }

    e.preventDefault();
    onInteractionStart?.();
    setPanPosition(constrainPan(newPan, maxPan));
  };

  const transition = hasInitialized && !isDragging && allowMotion ? 'transform 0.2s ease-out' : 'none';
  const {imageRef, onLoad} = useImageLoad()

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Interactive map of USA. Use arrow keys to pan, zoom controls to zoom."
      aria-roledescription="interactive map"
      {...(!isDummy && { tabIndex: 0 })}
      className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleTouchEnd}
      onDoubleClick={handleDoubleClick}
      style={{ cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'default', pointerEvents: transitionComplete ? 'auto' : 'none' }}
    >
      <div
        className="select-none relative bg-mint-green"
        style={{
          width: containedSize.width,
          height: containedSize.height,
          transform: `scale(${scale * zoomScale}) translate(${panPosition.x / (scale * zoomScale)}px, ${panPosition.y / (scale * zoomScale)}px)`,
          transition,
        }}
      >
        <Image 
          unoptimized={true}
          ref={imageRef}
          onLoad={onLoad}
          className="absolute inset-0 w-full h-full opacity-0"
          src={`${ASSET_PREFIX}/img/map.webp`}
          width={IMAGE_WIDTH}
          height={IMAGE_HEIGHT}
          alt="Map of USA"
          draggable={false}
          priority={true}
        />

        <div className="absolute w-full h-full">
          {stateLocations.map((state) => (
            <div
              key={state.abbreviation}
              className="absolute body-3-small font-medium whitespace-pre-line text-center uppercase text-[0.6rem] xl:text-[0.75rem]"
              style={{
                top: `${state.y}%`,
                left: `${state.x}%`,
                transform: `translate(-50%, -50%) scale(${1 / (scale * zoomScale)})`,
                transformOrigin: 'center center',
                transition,
              }}
            >
              {state.abbreviateAtZoom >= zoomLevel ? state.abbreviation : state.name}
            </div>
          ))}
        </div>

        {pinLocations.map((pin, index) => (
          <Pin
            key={`map-pin-${pin.location}-${index}`}
            ref={(el) => (pinsRef.current[index] = el)}
            className="absolute z-10 pointer-events-auto"
            top={pin.y}
            left={pin.x}
            label={pin.location}
            scale={1 / (scale * zoomScale)}
            transition={transition}
            initiallyHidden
            onClick={() => onPinClick?.(index, pin)}
            onFocus={() => onPinFocus?.(index, pin)}
            onMouseEnter={() => onPinMouseEnter?.(index, pin)}
            onMouseLeave={() => onPinMouseLeave?.(index, pin)}
            isSelected={selectedPinIndex === index}
            popoverId={POPOVER_ID}
          />
        ))}
      </div>
    </div>
  )
});export default Map;